import { Capacitor } from '@capacitor/core';
import {
  Alert,
  CircularProgress,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  OrderModel,
  OrderStatusModel,
  orderClient,
} from '@merchant-clients/order';

import { SideDrawer } from '@frontend/components';
import { useClientQuery, useLocale } from '@frontend/hooks';
import { tOrder } from '@frontend/locales';

import { OrderInfo } from './order-info.component';
import { mapOrderInfoToOrder } from './order-info-to-order.mapper';

const orderVariantMap = new Map<
  OrderStatusModel,
  'neutral' | 'warning' | 'success' | 'error'
>([
  ['PENDING', 'neutral'],
  ['CONFIRMED', 'warning'],
  ['READY', 'success'],
  ['CLOSED', 'error'],
  ['REJECTED', 'error'],
  ['REFUNDED', 'error'],
]);

export interface OrderDetailsDrawerProps {
  orders: OrderModel[];
  openState: boolean;
  toggleDrawer: () => void;
  /** Whether to reserve height for the live order-action-bar. Set to `false` on views without it (e.g. Order History). */
  reserveActionBarHeight?: boolean;
}

export const OrderDetailsDrawer = ({
  orders,
  openState,
  toggleDrawer,
  reserveActionBarHeight = true,
}: OrderDetailsDrawerProps) => {
  const [searchParams] = useSearchParams();
  const orderIdParam = searchParams.get('orderId');
  const theme = useTheme();
  const { formatters } = useLocale();

  const listedOrder = useMemo(() => {
    if (!orderIdParam) {
      return undefined;
    }

    return orders.find((order) => order.id === orderIdParam);
  }, [orders, orderIdParam]);

  const {
    data: fetchedOrderInfo,
    isFetching: isFetchingOrder,
    isError: isFetchOrderError,
  } = useClientQuery({
    queryKey: ['order', orderIdParam ?? ''],
    enabled: openState && !!orderIdParam && !listedOrder,
    onFetch: () => orderClient.get(orderIdParam!),
  });

  const selectedOrder = useMemo(() => {
    if (listedOrder) {
      return listedOrder;
    }

    if (fetchedOrderInfo) {
      return mapOrderInfoToOrder(fetchedOrderInfo);
    }

    return undefined;
  }, [listedOrder, fetchedOrderInfo]);

  const isDrawerOpen =
    openState && !!orderIdParam && (!!selectedOrder || isFetchingOrder);

  return (
    <SideDrawer
      openState={isDrawerOpen}
      toggleDrawer={() => {
        // Escape and the backdrop close it (drawerProps below); swipes and
        // back presses do not.
      }}
      drawerProps={{
        anchor: 'right',
        hideBackdrop: false,
        elevation: 0,
        sx: {
          zIndex: theme.zIndex.drawer + 1,
        },
        onKeyDown: (e) => {
          if (e.key === 'Escape') {
            toggleDrawer();
          }
        },
        slotProps: {
          backdrop: {
            sx: {
              background: 'none',
            },
            onClick: () => {
              toggleDrawer();
            },
          },
        },
      }}
      paperProps={{
        sx: {
          height:
            reserveActionBarHeight && Capacitor.isNativePlatform()
              ? 'calc(100% - calc(153px + var(--safe-area-inset-bottom)))' // '153px' is height of order-action-bar
              : '100%',
          p: 0,
        },
      }}
    >
      {isFetchingOrder && !selectedOrder && (
        <Stack alignItems="center" justifyContent="center" height="100%">
          <CircularProgress size={32} />
        </Stack>
      )}

      {isFetchOrderError && !selectedOrder && (
        <Stack alignItems="center" justifyContent="center" height="100%" px={3}>
          <Alert severity="error" sx={{ width: '100%' }}>
            {tOrder.orderHistory.loadOrderError()}
          </Alert>
        </Stack>
      )}

      {!!selectedOrder && (
        <Stack direction="column" height="100%" justifyContent="space-between">
          <Stack spacing={2}>
            <Stack
              sx={{
                backgroundColor:
                  theme.palette[orderVariantMap.get(selectedOrder.status)!]
                    .light,
                pt: `calc(var(--safe-area-inset-top) + ${theme.spacing(2)})`,
                px: theme.spacing(3),
                pb: theme.spacing(2),
              }}
            >
              <OrderInfo
                order={selectedOrder!}
                variant={orderVariantMap.get(selectedOrder.status)!}
                displayChildPlace
                cardSize="small"
              />
            </Stack>
            <Stack
              sx={{
                px: theme.spacing(3),
                pb: `max(var(--safe-area-inset-bottom), ${theme.spacing(4)})`,
              }}
            >
              <Stack direction="column" spacing={1}>
                {(selectedOrder.guestEmail ||
                  selectedOrder.guestName ||
                  selectedOrder.guestPhone) && (
                  <Stack direction="column">
                    {selectedOrder.guestName && (
                      <Typography variant="body2">
                        {selectedOrder.guestName}
                      </Typography>
                    )}
                    {selectedOrder.guestEmail && (
                      <Typography variant="body2" color="textSecondary">
                        {selectedOrder.guestEmail}
                      </Typography>
                    )}
                    {selectedOrder.guestPhone && (
                      <Typography variant="body2" color="textSecondary">
                        {selectedOrder.guestPhone}
                      </Typography>
                    )}
                  </Stack>
                )}

                {selectedOrder.message && (
                  <Alert severity="info" sx={{ wordWrap: 'break-word' }}>
                    {selectedOrder.message}
                  </Alert>
                )}
              </Stack>
              <List disablePadding>
                {selectedOrder.items.map((item, index) => (
                  <ListItem
                    key={`item-${item.id}-${index}`}
                    disablePadding
                    sx={{
                      minHeight: 0,
                      flexDirection: 'column',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      py: 2,
                      borderRadius: 0,
                    }}
                  >
                    <Stack direction="row" spacing={0.5} width="100%">
                      <Typography
                        variant="body2"
                        sx={{ minWidth: theme.spacing(2) }}
                      >
                        {item.quantity}
                      </Typography>
                      <Typography variant="body2">{item.name}</Typography>
                    </Stack>

                    {item.options && item.options.length > 0 && (
                      <List disablePadding sx={{ width: '100%' }}>
                        {item.options.map((option) => (
                          <ListItem
                            key={`option-${option.id}`}
                            disablePadding
                            sx={{ minHeight: 0, marginLeft: 2 }}
                          >
                            <Stack direction="row" spacing={0.5}>
                              <Typography
                                variant="caption"
                                component="p"
                                color="textSecondary"
                                sx={{ minWidth: theme.spacing(2) }}
                              >
                                {option.quantity}
                              </Typography>
                              <Typography
                                variant="caption"
                                component="p"
                                color="textSecondary"
                              >
                                {option.name}
                              </Typography>
                            </Stack>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </ListItem>
                ))}
              </List>
            </Stack>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              px: 3,
              py: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {tOrder.headers.total()}
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {formatters.currency(selectedOrder.total ?? 0)}
            </Typography>
          </Stack>
        </Stack>
      )}
    </SideDrawer>
  );
};
